import { appLocale } from "../../locales/display.ts";
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { useState } from "react";
import styled from "styled-components";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMobile } from "src/hooks/useMobile";

export interface IPagination {
	total: number;
	page: number;
	perPage: number;
	totalPages: number;
}

interface IPaginationProps {
	pagination: IPagination | undefined | null;
	onPageChange: (page: number) => void;
	showTotal?: boolean;
	disabled?: boolean;
}

const Navigation = styled.nav`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 10px 16px;
	min-width: 0;
	font-size: 13px;
	font-variant-numeric: tabular-nums;
`;
const Pages = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
`;
const PageButton = styled.button`
	box-sizing: border-box;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	min-width: 36px;
	height: 36px;
	padding: 0 9px;
	border: 1px solid transparent;
	border-radius: 8px;
	background: transparent;
	color: #475569;
	font: inherit;
	font-weight: 500;
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
	cursor: pointer;
	transition: background-color 120ms, border-color 120ms, color 120ms;
	&:hover:not(:disabled) { background: #e9eef5; color: #0f172a; }
	&[aria-current="page"] { background: #eff6ff; border-color: #bfdbfe; color: #1d4ed8; }
	&:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
	&:disabled { opacity: 0.4; cursor: default; }
	@media (pointer: coarse) { min-width: 40px; height: 40px; }
`;
const ArrowButton = styled(PageButton)`
	padding: 0;
	border-color: #e2e8f0;
	background: #fff;
`;
const Gap = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	color: #94a3b8;
	user-select: none;
`;
const PageStatus = styled.span`
	padding: 0 8px;
	color: #475569;
	white-space: nowrap;
`;
const Jump = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	label { color: #64748b; white-space: nowrap; }
	input {
		box-sizing: border-box;
		width: 70px;
		height: 36px;
		padding: 0 8px;
		border: 1px solid #dbe2ea;
		border-radius: 8px;
		background: #fff;
		color: #334155;
		font: inherit;
		text-align: center;
		font-variant-numeric: tabular-nums;
		appearance: textfield;
	}
	input::-webkit-inner-spin-button, input::-webkit-outer-spin-button { appearance: none; margin: 0; }
	input::placeholder { color: #94a3b8; }
	input:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
	input:disabled { opacity: 0.4; }
	@media (pointer: coarse) { input { height: 40px; } }
`;
const Total = styled.span`
	color: #64748b;
	white-space: nowrap;
	strong { color: #334155; font-weight: 500; }
`;
const formatNumber = (value: number) => value.toLocaleString(appLocale("en-US"));

// Keep a steady number of slots, including near the beginning and end of the list.
const visiblePages = (page: number, totalPages: number): (number | "…")[] => {
	const slots = 7;
	if (totalPages <= slots) return Array.from({ length: totalPages }, (_, index) => index);
	const neighbors = 1;
	if (page <= neighbors + 2) return [...Array.from({ length: slots - 2 }, (_, index) => index), "…", totalPages - 1];
	if (page >= totalPages - neighbors - 3) return [0, "…", ...Array.from({ length: slots - 2 }, (_, index) => totalPages - slots + 2 + index)];
	return [0, "…", ...Array.from({ length: neighbors * 2 + 1 }, (_, index) => page - neighbors + index), "…", totalPages - 1];
};

export const Pagination = ({ pagination, onPageChange, showTotal = true, disabled = false }: IPaginationProps) => {
	useLocale();
	const { isMobile } = useMobile();
	const [jump, setJump] = useState({ value: "", page: pagination?.page });
	const value = jump.page === pagination?.page ? jump.value : "";

	if (!pagination || pagination.totalPages <= 1) return null;

	const { page, perPage, total, totalPages } = pagination;
	const jumpPage = /^\d+$/.test(value) ? Number(value) - 1 : NaN;
	const canJump = Number.isSafeInteger(jumpPage) && jumpPage >= 0 && jumpPage < totalPages && jumpPage !== page;
	const changePage = (next: number) => {
		if (disabled || next === page || next < 0 || next >= totalPages) return;
		setJump({ value: "", page });
		onPageChange(next);
	};
	const go = () => { if (canJump) changePage(jumpPage); };

	return (
		<Navigation aria-label={msg("pagination")} aria-busy={disabled}>
			<Pages>
				<ArrowButton type="button" aria-label={msg("previousPage")} title={msg("previousPage")} disabled={disabled || page === 0} onClick={() => changePage(page - 1)}>
					<ChevronLeft size={16} aria-hidden="true" />
				</ArrowButton>
				{isMobile ? <PageStatus aria-current="page">{msg("page")}{formatNumber(page + 1)}{msg("of")}{formatNumber(totalPages)}</PageStatus>
					: visiblePages(page, totalPages).map((item, index) => item === "…"
					? <Gap key={`gap-${index}`} aria-hidden="true">…</Gap>
					: <PageButton type="button" key={item} aria-label={msg("templatePage_7cf4df60", { v0: formatNumber(item + 1) })} aria-current={item === page ? "page" : undefined}
						disabled={disabled} onClick={() => changePage(item)}>{formatNumber(item + 1)}</PageButton>)}
				<ArrowButton type="button" aria-label={msg("nextPage")} title={msg("nextPage")} disabled={disabled || page >= totalPages - 1} onClick={() => changePage(page + 1)}>
					<ChevronRight size={16} aria-hidden="true" />
				</ArrowButton>
			</Pages>
			{showTotal && <Total><strong>{formatNumber(Math.min(page * perPage + 1, total))}–{formatNumber(Math.min((page + 1) * perPage, total))}</strong>{msg("of")}{formatNumber(total)}</Total>}
			{totalPages > 7 && <Jump>
				<label>{msg("goTo")}<input type="number" inputMode="numeric" min={1} max={totalPages} step={1} placeholder={msg("page_0a30a815")} aria-label={msg("goToPage")}
					title={msg("templateEnterAPageFrom1To_25fa76cd", { v0: formatNumber(totalPages) })} value={value} disabled={disabled}
					onChange={event => setJump({ value: event.target.value, page })}
					onKeyDown={event => {
						if (event.key === "Enter") { event.preventDefault(); event.stopPropagation(); go(); }
						if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); setJump({ value: "", page }); }
					}} /></label>
				<ArrowButton type="button" disabled={disabled || !canJump} onClick={go} aria-label={msg("goToEnteredPage")} style={{ padding: "0 10px" }}>{msg("go")}</ArrowButton>
			</Jump>}
		</Navigation>
	);
};

export const ListPagination = ({ page, perPage, total, totalPages, hasNext, disabled, onPageChange }: {
	page: number; perPage: number; total?: number | null; totalPages?: number | null;
	hasNext: boolean; disabled?: boolean; onPageChange: (page: number) => void;
}) => {
	const pages = totalPages ?? (total != null ? Math.ceil(total / perPage) : page + (hasNext ? 2 : 1));
	return <Pagination pagination={{ page, perPage, total: total ?? 0, totalPages: pages }} onPageChange={onPageChange} disabled={disabled} showTotal={total != null} />;
};
